import { ENRICHMENT_GRADIENT_STYLE } from "@/components/company/shared/enrichment-gradient";
import { Progress } from "@/components/ui/progress";
import { useTranslation } from "@/hooks/useTranslation";
import type { CompanyWebPreviewProgress } from "@/stores/company-web-preview-store";
import { GlobeIcon } from "lucide-react";
import { motion, useReducedMotion, type TargetAndTransition } from "motion/react";

type RunPhase = CompanyWebPreviewProgress["phase"];

/** Share of the bar each phase of the run stands for. */
const PHASE_START: Record<RunPhase, number> = {
  searching: 8,
  reading: 15,
  writing: 92,
};
/** Share of the bar the reading phase fills, page by page. */
const READING_SPAN = 75;

function progressValue(
  phase: RunPhase,
  progress: CompanyWebPreviewProgress | undefined,
): number {
  if (phase !== "reading" || !progress) return PHASE_START[phase];
  const found = Math.max(progress.pagesFound, progress.pagesRead, 1);
  return Math.round(
    PHASE_START.reading + READING_SPAN * Math.min(1, progress.pagesRead / found),
  );
}

/**
 * How the card leaves once the profile is written: it blurs out where it
 * stands, then the room it took folds away so the profile slides up into it
 * rather than jumping — the margin too, which the panel's `space-y` puts there.
 */
const FOLD = { delay: 0.3, duration: 0.35, ease: [0.22, 1, 0.36, 1] } as const;
const BLUR_OUT: TargetAndTransition = {
  opacity: 0,
  filter: "blur(12px)",
  scale: 0.97,
  height: 0,
  paddingTop: 0,
  paddingBottom: 0,
  marginBottom: 0,
  transition: {
    default: { duration: 0.45, ease: [0.4, 0, 0.2, 1] },
    height: FOLD,
    paddingTop: FOLD,
    paddingBottom: FOLD,
    marginBottom: FOLD,
  },
};

const REDUCED_EXIT: TargetAndTransition = {
  opacity: 0,
  transition: { duration: 0 },
};

/**
 * Dark majorelle card shown while a web profile is being built: the phase of
 * the run, how many pages were read, and a progress bar. Same card as the
 * enrichment call-to-action so both read as "Shaarp is working on it", with
 * light drifting over it while the run lasts (`.enrichment-aurora`). Render it
 * inside an `AnimatePresence` so it blurs out when the run ends.
 */
export default function CompanyWebProgressCard({
  progress,
  writing,
}: {
  progress: CompanyWebPreviewProgress | undefined;
  /** True once the synthesis text streams, whatever the last progress event said. */
  writing: boolean;
}) {
  const { t } = useTranslation();
  const phase: RunPhase = writing
    ? "writing"
    : (progress?.phase ?? "searching");
  const pagesRead = progress?.pagesRead ?? 0;
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.div
      exit={shouldReduceMotion ? REDUCED_EXIT : BLUR_OUT}
      // No aria-label: it would replace the content, and the phase and the pages
      // read would never be announced.
      role="status"
      style={ENRICHMENT_GRADIENT_STYLE}
      className="relative isolate flex items-center gap-3 overflow-hidden rounded-xl p-3 text-white shadow-md"
    >
      <span aria-hidden className="enrichment-aurora" />
      <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-white/15">
        <GlobeIcon className="size-4 animate-pulse motion-reduce:animate-none" />
      </span>
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-semibold leading-tight">
          {t("company.webPreview.progress.title")}
        </h3>
        <p className="mt-0.5 text-xs leading-snug text-white/80">
          {t(`company.webPreview.progress.phase.${phase}`)}
          {pagesRead > 0 &&
            ` · ${t("company.webPreview.progress.pagesRead", { count: pagesRead })}`}
        </p>
        <Progress
          value={progressValue(phase, progress)}
          className="mt-2 h-1.5 bg-white/20 [&_[data-slot=progress-indicator]]:bg-white [&_[data-slot=progress-indicator]]:bg-none"
        />
      </div>
    </motion.div>
  );
}
